// /brokers/payouts — the broker payout batch review (SM-710, SM-621
// phase 07a; decision record SM-708 §7).
//
// ── what a batch is ──────────────────────────────────────────────────
//
// A broker's AVAILABLE revenue in one currency, prepared into a batch
// by hand — an operator prepares it for a node from "Brokers to settle"
// when they decide to (SM-878: there is no payout schedule; old batches
// still say WEEKLY / MONTHLY and "Scheduler") — and escrowed on the spot,
// so it can be neither spent, reassigned nor batched twice. It carries no
// destination: finance keeps the payment details outside Smart Market.
// It then waits here: a reviewer approves it — the one who prepared it
// may, and one approval is enough (SM-914; an old batch may still ask
// for two different reviewers) — finance pays it outside Smart Market
// and executes it, and someone confirms it with the bank reference or
// transaction hash — the escrow is burned and the batch is PAID.
// Rejecting, cancelling or marking it failed restores the escrow once,
// and the earnings go into the next batch.
//
// ── brokers to settle ────────────────────────────────────────────────
//
// Above the batches, each broker node with what it is owed in commission
// and owes in external credit (components/brokers/BrokersToSettle.tsx),
// and the three manual actions: Prepare payout, Request payment and
// Record payment sent. Nothing is netted or offset.
//
// ── one list ─────────────────────────────────────────────────────────
//
// Every batch, newest first, with state / broker / currency filters in
// the URL so a review view is shareable and a decision linkable. No
// default state: three states need a human (awaiting review, approved,
// processing) and the list filter takes one, so the default is all of
// them. Totals are per batch — one currency each, never summed.
//
// The route is /brokers/…, deliberately NOT under /admin: the dev
// server proxies the /admin prefix to the API, and a console page
// there would be swallowed by it.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";

import { BrokersToSettle } from "@/components/brokers/BrokersToSettle";
import { PayoutBatchDetail } from "@/components/brokers/PayoutBatchDetail";
import { BROKER_PAYOUT_CURRENCIES } from "@/components/brokers/governance";
import {
  actorLabel,
  approvalCount,
  batchStateLabel,
  batchStateTone,
  scheduleLabel,
} from "@/components/brokers/payouts";
import { canonToWire, msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { adminName, useAdminMap } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokerPayoutBatches, fetchBrokerSummary } from "@/lib/broker-queries";
import { PAYOUT_BATCH_STATES, type PayoutBatchItem } from "@/lib/broker-types";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 20;

interface Search {
  state?: string;
  broker?: string;
  currency?: string;
  page?: number;
  /** The batch whose panel is open. */
  open?: string;
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

// Currency codes are identifiers, not copy: the option shows the code.
// A batch is only ever prepared in a payout currency — no rail pays RED
// or GREEN (SM-708 Q4) — so those would only ever list nothing (SM-848),
// and an old link's ?currency=RED is dropped rather than obeyed.
const CURRENCIES: readonly string[] = BROKER_PAYOUT_CURRENCIES;
const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({ value: c, label: c }));

export const Route = createFileRoute("/_app/brokers/payouts")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    state: PAYOUT_BATCH_STATES.find((st) => st === s.state),
    broker: str(s.broker),
    currency: CURRENCIES.includes(String(s.currency)) ? String(s.currency) : undefined,
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
    open: str(s.open),
  }),
  component: BrokerPayoutsPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

const STATE_OPTIONS = [
  { value: "PENDING_REVIEW", label: "Awaiting review" },
  { value: "APPROVED", label: "Approved" },
  { value: "PROCESSING", label: "Processing" },
  { value: "DRAFT", label: "Draft" },
  { value: "PAID", label: "Paid" },
  { value: "FAILED", label: "Failed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

function BrokerPayoutsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const admins = useAdminMap();
  const canView = perms.has(Perm.BrokerRevenueView);

  const page = search.page ?? 0;
  const filter = {
    state: search.state,
    broker_id: search.broker,
    currency: search.currency,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const q = useQuery({
    queryKey: brokerKeys.payoutBatches(filter),
    queryFn: () => fetchBrokerPayoutBatches(filter),
    // A colleague's approval or execution should not still read as
    // awaiting review here.
    refetchInterval: 30_000,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];

  // Name each broker: one cached read per distinct broker on the page,
  // and only for an admin who may read brokers at all — without
  // BROKER_VIEW the id stands on its own.
  const canNameBrokers = perms.has(Perm.BrokerView);
  const brokerIDs = [...new Set(rows.map((r) => r.broker_id))];
  const names = useQueries({
    queries: brokerIDs.map((id) => ({
      queryKey: brokerKeys.broker(id),
      queryFn: () => fetchBrokerSummary(id),
      enabled: canNameBrokers,
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const brokerName = new Map<string, string>();
  names.forEach((n, i) => {
    const name = n.data?.display_name || n.data?.legal_name;
    if (name) brokerName.set(brokerIDs[i], name);
  });

  function apply(next: Partial<Search>) {
    void navigate({ search: (s) => ({ ...s, ...next, page: undefined }) });
  }
  function openPanel(id: string | undefined) {
    void navigate({ search: (s) => ({ ...s, open: id }) });
  }

  if (perms.loaded && !canView) {
    return (
      <EmptyState
        title="No access"
        description="You need the BROKER_REVENUE_VIEW permission to see broker payout batches."
      />
    );
  }

  const narrowed = Boolean(search.state || search.broker || search.currency);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Broker payouts</h1>
        <p className="text-sm text-muted">
          Each batch is one broker&apos;s available revenue in one currency, prepared by hand and held in escrow from
          the moment it was prepared. An admin approves it, finance pays it outside the platform
          and records the payment reference here. Rejecting, cancelling or a failed payment returns the money to the
          broker&apos;s available revenue for the next batch.
        </p>
      </div>

      <BrokersToSettle onOpenBatch={(id) => openPanel(id)} />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select
            label="State"
            value={search.state ?? ""}
            onChange={(e) => apply({ state: e.target.value || undefined })}
            placeholder="Any state"
            options={STATE_OPTIONS}
          />
        </div>
        <div className="w-56">
          <Input
            label="Broker"
            placeholder="Broker id…"
            defaultValue={search.broker ?? ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply({ broker: str((e.target as HTMLInputElement).value) });
            }}
            onBlur={(e) => apply({ broker: str(e.target.value) })}
          />
        </div>
        <div className="w-40">
          <Select
            label="Currency"
            value={search.currency ?? ""}
            onChange={(e) => apply({ currency: e.target.value || undefined })}
            placeholder="Any currency"
            options={CURRENCY_OPTIONS}
          />
        </div>
        {/* Its own line under the bar rather than the picker's hint: the
            filters are bottom-aligned, and a two-line control would drag
            every neighbour down with it. */}
        <p className="basis-full text-2xs text-muted">
          Only GOLD is listed: payout batches are paid in GOLD only, because no payout rail pays RED or GREEN.
        </p>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Payout batches</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {(q.isLoading || !perms.loaded) && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}
          {q.data && rows.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">
              {narrowed
                ? "No payout batches match the current filters."
                : "No payout batches yet. An admin prepares one by hand from Brokers to settle."}
            </p>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>State</th>
                    <th className={th}>Currency</th>
                    <th className={`${th} text-right`}>Total</th>
                    <th className={`${th} text-right`}>Items</th>
                    <th className={th}>Approvals</th>
                    <th className={th}>Schedule</th>
                    <th className={th}>Cutoff</th>
                    <th className={th}>Requested by</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <BatchRow
                      key={b.batch_id}
                      b={b}
                      name={brokerName.get(b.broker_id)}
                      requester={actorLabel(b.requested_by, (id) => adminName(admins, id))}
                      onOpen={() => openPanel(b.batch_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {q.data && q.data.page.total > 0 && (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
              itemLabel="entries"
            />
          )}
        </CardBody>
      </Card>

      {search.open ? (
        <PayoutBatchDetail
          key={search.open}
          batchID={search.open}
          fallback={rows.find((r) => r.batch_id === search.open)}
          onClose={() => openPanel(undefined)}
        />
      ) : null}
    </div>
  );
}

function BatchRow({
  b,
  name,
  requester,
  onOpen,
}: {
  b: PayoutBatchItem;
  name?: string;
  requester: string;
  onOpen: () => void;
}) {
  const approvals = approvalCount(b);
  const short = b.state === "PENDING_REVIEW" && approvals < b.approvals_required;
  return (
    <tr
      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      tabIndex={0}
    >
      <td className={`${td} px-5`}>
        {name ? <div className="font-medium">{name}</div> : null}
        <div className="font-mono text-2xs text-muted">{b.broker_id}</div>
      </td>
      <td className={td}>
        <Badge tone={batchStateTone(b.state)}>{batchStateLabel(b.state)}</Badge>
      </td>
      <td className={td}>
        <CurrencyBadge currency={b.currency} />
      </td>
      <td className={`${td} text-right`}>
        <CurrencyAmount currency={b.currency} value={canonToWire(b.total)} />
      </td>
      <td className={`${td} text-right`}>{b.item_count}</td>
      <td className={cn(td, short && "font-semibold text-warning")}>
        {approvals} / {b.approvals_required}
      </td>
      <td className={td}>{scheduleLabel(b.schedule)}</td>
      <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(b.cutoff_unix_ms))}</td>
      <td className={`${td} text-2xs`}>{requester}</td>
    </tr>
  );
}
